/// <reference types="@spazious/dollhouse-types/generics" />

import { EventEmitter } from 'events';

import { traverse, uuid } from '@spazious/dollhouse-utils';

import {
  GraphChildNode,
  GraphNode,
  GraphNodeEvents,
  GraphNodeJSON,
  GraphParentNode,
  GraphRootNode,
} from './types.js';
import { isGraph } from './utils.js';

export class GraphNodeImpl<
    TRoot extends GraphRootNode,
    TParent extends GraphParentNode,
    TChild extends GraphChildNode
  >
  extends EventEmitter<GraphNodeEvents>
  implements GraphNode<TRoot, TParent, TChild>
{
  uuid: string;
  type: string;
  root: TRoot | null;
  parent: TParent | null;
  children: TChild[] = [];
  evaluateRoot: boolean = true;

  get isGraphNode(): true {
    return true;
  }

  /**
   * Create a new graph node
   */
  constructor();
  constructor(type: string);
  constructor(type?: string) {
    super();
    this.type = type ?? '';
    this.uuid = uuid();
    this.parent = null;
    this.root = this as any;
  }

  /**
   * Append a child node to the children list
   */
  appendChild(node: TChild) {
    // const index = this.children.length;
    this.children.push(node);
    node.parent = this;
    node.root = this.root;
    // TODO this is no working properly when index is specified
    this.refreshChildrenRefs(0);
    node.emit('added');
    return this;
  }

  /**
   * Inserts a child node before a reference node
   */
  insertBefore(node: TChild, referenceNode: TChild) {
    const index = this.children.indexOf(referenceNode);
    if (index >= 0) {
      this.children.splice(index, 0, node);
      node.parent = this;
      // TODO this is no working properly when index is specified
      this.refreshChildrenRefs(0);
      node.emit('added');
    }
    return this;
  }

  /**
   * Removes a certain child node from the children list
   */
  removeChild(node: TChild) {
    const index = this.children.indexOf(node);
    if (index >= 0) {
      this.children.splice(index, 1);
      node.parent = null;
      // TODO this is no working properly when index is specified
      this.refreshChildrenRefs(0);
      node.emit('removed');
    }
    return this;
  }

  /**
   * Remove the node from the parent
   */
  remove() {
    if (this.parent) {
      this.parent.removeChild(this);
    }
    return this;
  }

  /**
   * Clean all children nodes
   */
  clean() {
    this.children.forEach((child) => child.remove());
    return this;
  }

  /**
   * Refresh the children references such as selector, root and parent nodes
   */
  refreshChildrenRefs(start: number = 0, recursive = true) {
    let root: TRoot = (this.parent ?? this) as unknown as TRoot;
    for (let i = start; i < this.children.length; i++) {
      const child = this.children[i];
      if (this.evaluateRoot) {
        if (child.root !== this.root) {
          for (; root?.parent; ) {
            root = root.parent as TRoot;
          }
        }
        if (isGraph(root)) {
          child.root = root;
        }
      }
      child.refreshChildrenRefs(0, recursive);
    }
    return this;
  }

  /**
   * Subscribe to the update event
   */
  subscribe(callback: () => void): () => void {
    this.on('update', callback);
    return () => {
      this.off('update', callback);
    };
  }

  /**
   * Find a node by its UUID
   */
  findByUUID(uuid: string): TChild | null {
    if (this.uuid === uuid) {
      return this as unknown as TChild;
    }
    for (let i = 0; i < this.children.length; i++) {
      const child = this.children[i];
      const found = child.findByUUID(uuid);
      if (found) {
        return found as TChild;
      }
    }
    return null;
  }

  /**
   * Convert the node to a JSON representation
   */
  toJSON(recursive = true): GraphNodeJSON {
    return {
      uuid: this.uuid,
      type: this.type,
      children: recursive ? this.children.map((child) => child.toJSON(recursive)) : [],
    };
  }

  /**
   * Update the node from a JSON representation
   */
  fromJSON(json: any) {
    if (this.type && this.type !== json.type) {
      throw new Error(`Cannot update node with different type "${this.type}" !== "${json.type}"`);
    }
    this.type = json.type;
    this.uuid = json.uuid ?? this.uuid;

    if (Array.isArray(json.children) && json.children.length) {
      const Ctor = this.constructor as ConstructorType<any>;
      this.children = json.children.map((child: any) => new Ctor().fromJSON(child));
    }

    return this;
  }

  /**
   * Traverse the graph and apply a callback to each node
   */
  traverse(callback: (node: TChild) => void): void {
    traverse(this as any, callback);
  }

  /**
   * Flatten the graph into a list of nodes
   */
  flatten(): TChild[] {
    const nodes: TChild[] = [];
    this.traverse((node) => nodes.push(node));
    return nodes;
  }
}

export const createGraphNode = (type: string) => new GraphNodeImpl(type);
